import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import { sprintf } from "utils/strings";

import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";
import ImportExcelDialog from "components/Common/ImportExcelDialog";

import SchemaDialogForm from "components/Form/SchemaDialogForm"; // adjust path to your actual file
import CardsTableData from "./data/CardsTableData";
import { cardsFormSchema, cardsRecord } from "./data/CardsSchemas";
import { handleApiError } from "utils/apiError";

import UploadIcon from "@mui/icons-material/Upload";

function Cards() {
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(cardsRecord);
  const [dialogTitle, setDialogTitle] = useState("Create Card");
  const [saving, setSaving] = useState(false);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarColor, setSnackBarColor] = useState("error");
  const [snackBarIcon, setSnackBarIcon] = useState("warning");
  const [snackBarTitle, setSnackBarTitle] = useState("Error");
  const [snackErrorMsg, setSnackErrorMsg] = useState("");
  const [confirmationDialogMessage, setConfirmationDialogMessage] = useState("");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActions, setBulkActions] = useState([
    { key: "select_all", text: "Select All", icon: "select_all", iconColor: "text" },
    { key: "unselect_all", text: "Unselect All", icon: "deselect", iconColor: "text" },
    { key: "delete_selected", text: "Delete", icon: "delete", iconColor: "error" },
  ]);
  const [searchInput, setSearchInput] = useState("");

  const showSnackBar = (title, type, message) => {
    setSnackBarColor(type === "error" ? "error" : "success");
    setSnackBarIcon(type === "error" ? "warning" : "check_circle");
    setSnackBarTitle(title);
    setSnackErrorMsg(message);
    setSnackBarOpen(true);
    setTimeout(() => setSnackBarOpen(false), 3000);
  };

  const handleToggleCheckbox = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    setSelectedIds([]);
  }, [page]);

  const handleSearchFieldKeyUp = (event) => {
    if (event.key === "Enter") {
      loadCards(searchInput);
    }
  };

  const handleSearchOnClick = async () => {
    loadCards(searchInput);
  };

  const loadCards = async (q) => {
    try {
      setLoading(true);
      let url;
      if (q.length > 0) {
        url = `${apiBase}/api/cards/search-list?page=${page}&size=${rowsPerPage}&q=${q}`;
      } else {
        url = `${apiBase}/api/cards?page=${page}&size=${rowsPerPage}`;
      }
      const res = await httpClient.get(url);

      setItems(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);

      // if your backend returns these too, keep them:
      setRowsPerPage(res.data.size || rowsPerPage);
      setPage(res.data.page || page);
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });

      if (!handled) throw err; // usually won't happen with the handler above
      return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCards("");
  }, [page, rowsPerPage]);

  const openCreateDialog = () => {
    setEditingRecord({
      ...cardsRecord,
    });
    setDialogTitle("Create Card");
    setDialogOpen(true);
  };

  const openEditDialog = (rec) => {
    setEditingRecord({
      ...rec,
    });
    setDialogTitle("Edit Card");
    setDialogOpen(true);
  };

  const onCloseDialog = () => {
    setDialogOpen(false);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = { ...data };

      const isEdit = payload.id && Number(payload.id) > 0;

      if (isEdit) {
        await httpClient.put(`${apiBase}/api/cards/${payload.id}`, payload);
        showSnackBar("Success", "success", "Card updated");
      } else {
        await httpClient.post(`${apiBase}/api/cards`, payload);
        showSnackBar("Success", "success", "Card created");
      }

      setDialogOpen(false);
      await loadCards("");
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });

      if (!handled) throw err; // usually won't happen with the handler above
      return;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (rec) => {
    setItemToDelete(rec);
    setConfirmationDialogMessage(sprintf(t("confirmation.toDeleteItem"), [`Card (${rec.name})`]));
    setConfirmationDialogOpen(true);
  };

  const onDeleteConfirmed = async () => {
    try {
      const auth = localStorage.getItem("auth");
      let parsedAuth = null;
      if (auth) {
        try {
          parsedAuth = JSON.parse(auth);
        } catch {
          // ignore
          return;
        }
      }
      let res;
      if (Array.isArray(itemToDelete)) {
        res = await httpClient.post(
          `${apiBase}/api/cards/bulk-delete`,
          { ids: itemToDelete },
          {
            headers: {
              Authorization: `Bearer ${parsedAuth.token}`,
            },
          }
        );
      } else {
        res = await httpClient.delete(`${apiBase}/api/cards/${itemToDelete.id}`, {
          headers: {
            Authorization: `Bearer ${parsedAuth.token}`,
          },
        });
      }
      if (res.status == 204) {
        showSnackBar(
          t("successMsg.operationTitleSuccess"),
          "success",
          sprintf(t("successMsg.itemDeleteSuccessfully"), ["Card(s)"])
        );
        await loadCards("");
      }
    } catch (err) {
      const { handled } = handleApiError(err, {
        showSnackBar,
        onAuthError: null, // will also be called for 401 if you want
      });

      if (!handled) throw err; // usually won't happen with the handler above
      return;
    } finally {
      setConfirmOpen(false);
      setConfirmationDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteBulk = async (ids) => {
    setItemToDelete(ids);
    setConfirmationDialogMessage(sprintf(t("confirmation.toDeleteItem"), ["Card(s)"]));
    setConfirmationDialogOpen(true);
  };

  const actionsMenuItemClicked = async (key) => {
    if (key.includes("Delete")) {
      handleDeleteBulk(selectedIds);
      return;
    } else if (key.includes("Select All")) {
      handleSelectAll();
    } else if (key.includes("Unselect All")) {
      handleUnSelectAll();
    }
  };

  const handleSelectAll = () => {
    for (let i = 0; i < items.length; i++) {
      if (selectedIds.includes(items[i].id)) {
        continue;
      }
      handleToggleCheckbox(items[i].id);
    }
  };

  const handleUnSelectAll = () => {
    setSelectedIds([]);
  };

  const renderSB = (
    <MDSnackbar
      color={snackBarColor}
      icon={snackBarIcon}
      title={snackBarTitle}
      content={snackErrorMsg}
      dateTime=""
      open={snackBarOpen}
      onClose={() => setSnackBarOpen(false)}
      close={() => setSnackBarOpen(false)}
      bgWhite
    />
  );

  const selectOptionsMap = {};

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <MDBox alignItems="left" display="flex" gap={1}>
                <MDButton variant="gradient" color="info" size="small" onClick={openCreateDialog}>
                  Create Card <Icon>add</Icon>
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  onClick={() => setImportOpen(true)}
                >
                  Import Excel <UploadIcon fontSize="small" />
                </MDButton>
              </MDBox>
              <MDBox alignItems="right" display="flex">
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDInput
                    size="small"
                    label={sprintf(t("search.searchInModule"), ["Cards"])}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyUp={handleSearchFieldKeyUp}
                  />
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={handleSearchOnClick}
                  >
                    {t("search.go")}
                  </MDButton>
                </MDBox>
                <CustomMenuButton
                  items={bulkActions}
                  onItemClicked={(item) => actionsMenuItemClicked(item)}
                  buttonTitle={"Actions"}
                />
              </MDBox>
            </MDBox>

            <MDBox px={3} pb={3}>
              {loading ? (
                <MDTypography variant="button">Loading...</MDTypography>
              ) : items.length === 0 ? (
                <MDTypography variant="button">No card found.</MDTypography>
              ) : (
                <div>
                  <CardsTableData
                    data={items}
                    selectedIds={selectedIds}
                    onToggle={handleToggleCheckbox}
                    onEdit={openEditDialog}
                    onDelete={handleDelete}
                  />

                  <CustomTablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalItems={totalElements}
                    onPageChange={(newPage) => setPage(newPage)}
                    onRowsPerPageChange={(n) => {
                      setRowsPerPage(n);
                      setPage(0);
                    }}
                  />
                </div>
              )}
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>

      {renderSB}

      <ConfirmationDialog
        open={confirmationDialogOpen}
        title={t("confirmation.confirm")}
        message={confirmationDialogMessage}
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
      />

      <ImportExcelDialog
        open={importOpen}
        title="Import Cards"
        endpoint={`${apiBase}/api/cards/import`}
        templateEndpoint={`${apiBase}/api/cards/template`}
        onClose={() => setImportOpen(false)}
        onDone={() => loadCards("")}
        showSnackBar={showSnackBar}
      />

      <SchemaDialogForm
        open={dialogOpen}
        title={dialogTitle}
        schema={cardsFormSchema}
        initialRecord={editingRecord}
        saving={saving}
        onClose={onCloseDialog}
        onSubmit={onSubmit}
        deps={selectOptionsMap}
      />
    </DashboardLayout>
  );
}

export default Cards;
